import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import NotificationTemplateService from "../../services/NotificationTemplateService";
import RenderOnRole from "../../components/RenderOnRole";

const TYPE_COLORS = {
  EMAIL: "primary",
  SMS: "success",
  VOICE: "warning",
  APP: "info",
};

const STATUS_COLORS = {
  ACTIVE: "success",
  INACTIVE: "default",
};

export default function NotificationTemplateList() {
  const navigate = useNavigate();

  const [filterProcess, setFilterProcess] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterNotifType, setFilterNotifType] = useState("");

  const { data: allRows, isLoading } = useQuery(
    ["notification-templates"],
    () => NotificationTemplateService.list(),
    { select: (r) => r.data }
  );

  const { data: notificationTypes = [] } = useQuery(
    ["notification-types"],
    () => NotificationTemplateService.getNotificationTypes(),
    { select: (r) => r.data }
  );

  const rows = useMemo(() => {
    if (!allRows) return [];
    return allRows.filter((row) => {
      if (
        filterProcess &&
        !row.idProcess?.toLowerCase().includes(filterProcess.toLowerCase())
      )
        return false;
      if (filterType && row.typeTemplate !== filterType) return false;
      if (filterNotifType && row.notificationTypeId !== filterNotifType)
        return false;
      return true;
    });
  }, [allRows, filterProcess, filterType, filterNotifType]);

  const columns = [
    {
      field: "idNotificationTemplate",
      headerName: "ID",
      width: 70,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "idProcess",
      headerName: "Process",
      flex: 2,
      minWidth: 160,
    },
    {
      field: "typeTemplate",
      headerName: "Type",
      width: 100,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value}
          color={TYPE_COLORS[value] || "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "lang",
      headerName: "Lang",
      width: 70,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => value || "—",
    },
    {
      field: "idEmployer",
      headerName: "Employer",
      width: 130,
      renderCell: ({ value }) =>
        value ? (
          <span>{value}</span>
        ) : (
          <Chip size="small" label="ALL" color="info" />
        ),
    },
    {
      field: "notificationTypeId",
      headerName: "Notif. Type",
      width: 140,
      renderCell: ({ value }) => value || "—",
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value}
          color={STATUS_COLORS[value] || "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          startIcon={<EditIcon fontSize="small" />}
          onClick={() =>
            navigate(
              `/communication/notification-templates/${row.idNotificationTemplate}`
            )
          }
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6">Notification Templates</Typography>
        <RenderOnRole roles={["pc-program-super-admin"]}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              navigate("/communication/notification-templates/new")
            }
          >
            New Template
          </Button>
        </RenderOnRole>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          label="Search process"
          value={filterProcess}
          onChange={(e) => setFilterProcess(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Template Type</InputLabel>
          <Select
            label="Template Type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {["EMAIL", "SMS", "VOICE", "APP"].map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Notification Type</InputLabel>
          <Select
            label="Notification Type"
            value={filterNotifType}
            onChange={(e) => setFilterNotifType(e.target.value)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {notificationTypes.map((nt) => (
              <MenuItem
                key={nt.notificationTypeId}
                value={nt.notificationTypeId}
              >
                {nt.notificationTypeId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(r) => r.idNotificationTemplate}
        loading={isLoading}
        components={{ Toolbar: GridToolbar }}
        componentsProps={{ toolbar: { showQuickFilter: true } }}
        rowsPerPageOptions={[15, 50, 100]}
        initialState={{ pagination: { pageSize: 15 } }}
        disableSelectionOnClick
      />
    </Box>
  );
}
